import { Fragment, type ReactElement, type ReactNode } from "react";
import {
  isCountKnown,
  matchQueryRenderState,
  type QueryRenderResult,
  type QueryRenderState,
} from "src/domains/shared/utils/queryRenderState";

export interface QueryStateViewProps {
  /** `resolveQueryRenderState` 가 산출한 판정. 손으로 만든 값을 넣지 않는다. */
  renderState: QueryRenderResult;
  /**
   * 갈래별 렌더. `Record<QueryRenderState, ...>` 이므로 갈래가 늘면 컴파일이 깨지고,
   * `matchQueryRenderState` 가 실제로 인덱싱하므로 강제력이 런타임까지 유지된다.
   */
  branches: Record<QueryRenderState, () => ReactNode>;
  /**
   * `degraded` 일 때 갈래 렌더 **위에** 얹히는 국소 통지(인라인 배너 등).
   *
   * **선택 인자로 두지 않는다** — 빠뜨린 지점이 조용히 무증상 실패로 떨어지는 것이
   * 이 컴포넌트가 막으려는 결함 그 자체다. 통지를 둘 자리가 정말 없으면 `null` 을
   * 명시적으로 넘겨 그 판단이 코드에 남게 한다.
   */
  degradedNotice: ReactNode;
}

/**
 * 렌더 게이트 4갈래 + 잔존 결과 2단 가드의 아랫단을 **한 자리에서** 그린다.
 * 계약 SoT 는 `query-failure-state-policy` 스펙.
 *
 * 존재 이유는 배치 실수를 API 로 막는 것이다. `matchQueryRenderState` 만 쓰면 저하 통지를
 * `ready` 갈래 **안**에 두는 것이 가장 자연스러운 손놀림인데, `hasData` 가 필수가 된 뒤로는
 * `empty + degraded`("성공으로 0건임을 확인한 뒤 재조회 실패")가 대부분의 지점에서 도달
 * 가능하므로 그 배치는 실패를 **완전히 조용하게** 만든다 — 화면은 "0건" 을 확정 사실로
 * 주장하는데 실제로는 마지막 조회가 실패한 상태다. 여기서는 통지 자리가 갈래 매핑 바깥에
 * 고정돼 있어 `empty` 와 `ready` 양쪽에 자동으로 얹힌다.
 *
 * **`pending`·`error` 에 얹지 않는 것도 계약이다.** 그 두 갈래는 이미 자기 표현(로딩 표시,
 * 오류 문구 + 재시도)을 갖고 있어 통지가 중복되고, `degraded` 는 정의상 확보한 결과가 있을
 * 때만 참이라 그 두 갈래와 함께 성립하지도 않는다.
 *
 * ```tsx
 * <QueryStateView
 *   renderState={listState}
 *   degradedNotice={loadFailedAlert}
 *   branches={{
 *     pending: () => <CircularProgress />,
 *     error: () => loadFailedAlert,
 *     empty: () => <EmptyGuide />,
 *     ready: () => <List items={items} />,
 *   }}
 * />
 * ```
 *
 * 갈래 렌더가 `ReactNode` 가 아닌 소비처(지도 마커와 통지처럼 **한 갈래가 두 자리를**
 * 채워야 하는 경우)는 이 컴포넌트를 쓸 수 없다 — `matchQueryRenderState` 를 직접 쓰고
 * `empty` 갈래에도 `ready` 와 같은 통지 식을 넣는다.
 */
export function QueryStateView({
  renderState,
  branches,
  degradedNotice,
}: QueryStateViewProps): ReactElement {
  // 얹히는 갈래를 손으로 열거하지 않고 공용 술어 `isCountKnown`(개수를 아는 갈래 =
  // `empty`·`ready`)을 쓴다 — 위 계약의 "확보한 결과가 있을 때만" 이 판정과 같은 자리에서
  // 유지된다.
  const showsDegradedNotice = renderState.degraded && isCountKnown(renderState);

  return (
    <Fragment>
      {showsDegradedNotice && degradedNotice}
      {matchQueryRenderState(renderState.state, branches)}
    </Fragment>
  );
}
